/**
 * A converter which converts Fractional Price
 * types to Decimal and validates based on the tick size provided.
 *
 * @author  E42303 Judhjit Ganguli
 */
const decimal = require("decimal.js");

// Helper function to format numerator with leading zeros
const formatNumerator = (num, digits) => {
  return String(num).padStart(digits, "0");
};

const EPSILON = 1e-9; // Small value for float comparisons

export function DecimalToFractionalConverter(
  decimalValueInput,
  fractionalType,
) {
  if (
    decimalValueInput === null ||
    decimalValueInput === undefined ||
    isNaN(parseFloat(decimalValueInput))
  ) {
    return ""; // Or handle error appropriately
  }

  const dValue = new decimal(decimalValueInput);
  const absDValue = dValue.abs();
  const integerPart = absDValue.floor();
  let fractionalPartDecimal = absDValue.minus(integerPart);
  const sign = dValue.isNegative() ? "-" : "";

  if (fractionalPartDecimal.abs().lt(EPSILON)) {
    // Essentially zero fractional part
    return sign + integerPart.toString();
  }

  let fractionalString = "";

  switch (fractionalType) {
    case "NotFractional":
      return dValue.toString(); // Return as is, or format to a specific DP if needed
    case "Halves": {
      // Numerator will be 0 or 1 for 0/2 or 1/2
      const numerator = fractionalPartDecimal.mul(2).round();
      if (numerator.eq(1)) {
        fractionalString = integerPart.toString() + "'5";
      } else {
        // numerator is 0 or 2 (which means it was a whole number or 0.0)
        return sign + integerPart.toString();
      }
      break;
    }
    case "Eighths": {
      const numerator = fractionalPartDecimal.mul(8).round();
      if (numerator.isZero()) {
        return sign + integerPart.toString();
      }
      fractionalString = integerPart.toString() + "'" + numerator.toString();
      break;
    }
    case "ThirtySeconds": {
      const numerator = fractionalPartDecimal.mul(32).round();
      if (numerator.isZero()) {
        return sign + integerPart.toString();
      }
      fractionalString =
        integerPart.toString() + "'" + formatNumerator(numerator.toNumber(), 2);
      break;
    }
    case "SixtyFourths": {
      const numerator = fractionalPartDecimal.mul(64).round();
      if (numerator.isZero()) {
        return sign + integerPart.toString();
      }
      // Regex implies up to 3 digits, but typical usage is 1 or 2 (e.g. 0'1, 0'63)
      // The original parser's `*100` logic for SixtyFourths is a bit ambiguous for reversal.
      // Sticking to 2 digits for numerator based on common interpretation of 'NN/64'.
      fractionalString =
        integerPart.toString() + "'" + formatNumerator(numerator.toNumber(), 2);
      break;
    }
    case "HalfThirtySeconds": {
      // Denominator 32, sub-denominator 2 (for 0.5 part)
      const val = fractionalPartDecimal.mul(32); // e.g., 1.5 for 1/32 + 0.5/32
      const mainNum = val.floor();
      const subVal = val.minus(mainNum); // e.g., 0.5

      let subDigitStr = "";
      if (subVal.abs().sub(0.5).abs().lt(EPSILON)) {
        subDigitStr = "5";
      } else if (!subVal.abs().lt(EPSILON)) {
        // Not 0.0 and not 0.5
        return dValue.toString(); // Invalid for this type
      }
      if (mainNum.isZero() && subDigitStr === "") {
        return sign + integerPart.toString();
      }
      fractionalString =
        integerPart.toString() + "'" + mainNum.toString() + subDigitStr;
      break;
    }
    case "QuarterThirtySeconds": {
      // Denominator 32, sub-unit 0.25
      const val = fractionalPartDecimal.mul(32);
      const mainNum = val.floor();
      const subDecimalVal = val.minus(mainNum); // 0.0, 0.25, 0.5, 0.75

      let subDigitStr = "";
      if (subDecimalVal.abs().sub(0.25).abs().lt(EPSILON)) subDigitStr = "2";
      else if (subDecimalVal.abs().sub(0.5).abs().lt(EPSILON))
        subDigitStr = "5";
      else if (subDecimalVal.abs().sub(0.75).abs().lt(EPSILON))
        subDigitStr = "7";
      else if (!subDecimalVal.abs().lt(EPSILON)) {
        // Not 0.0 and not a valid quarter
        return dValue.toString(); // Invalid for this type
      }
      if (mainNum.isZero() && subDigitStr === "") {
        return sign + integerPart.toString();
      }
      fractionalString =
        integerPart.toString() + "'" + mainNum.toString() + subDigitStr;
      break;
    }
    case "EighthThirtySeconds": {
      // Denominator 32, sub-unit 0.125
      const val = fractionalPartDecimal.mul(32);
      const mainNum = val.floor();
      const subDecimalVal = val.minus(mainNum); // N * 0.125

      const subDigit = subDecimalVal.div(0.125).round();
      // Ensure subDigit is within 0-7 range if it's derived this way.
      // The original conversion logic for this was complex, this is a simplified reverse.
      if (
        subDigit.lt(0) ||
        subDigit.gt(7) ||
        !subDecimalVal.div(0.125).minus(subDigit).abs().lt(EPSILON)
      ) {
        // If subDigit is not a clean multiple of 0.125 or out of range
        if (!subDecimalVal.abs().lt(EPSILON)) {
          // and not exactly zero
          return dValue.toString(); // Invalid
        }
      }
      const subDigitStr = subDigit.eq(0) ? "" : subDigit.toString();
      if (mainNum.isZero() && subDigitStr === "") {
        return sign + integerPart.toString();
      }
      fractionalString =
        integerPart.toString() + "'" + mainNum.toString() + subDigitStr;
      break;
    }
    case "HalfSixtyFourths": {
      // Denominator 64, sub-denominator 2
      const val = fractionalPartDecimal.mul(64);
      const mainNum = val.floor();
      const subVal = val.minus(mainNum);

      let subDigitStr = "";
      if (subVal.abs().sub(0.5).abs().lt(EPSILON)) {
        subDigitStr = "5";
      } else if (!subVal.abs().lt(EPSILON)) {
        return dValue.toString(); // Invalid
      }
      if (mainNum.isZero() && subDigitStr === "") {
        return sign + integerPart.toString();
      }
      fractionalString =
        integerPart.toString() +
        "'" +
        formatNumerator(mainNum.toNumber(), 2) +
        subDigitStr;
      break;
    }
    case "Quarters":
    case "Sixteenths":
      // Not implemented as per original file
      return (
        dValue.toString() +
        " (Conversion for " +
        fractionalType +
        " not implemented)"
      );
    default:
      return dValue.toString() + " (Unknown FractionalType)";
  }

  return sign + fractionalString;
}
